import { Server, Network } from '../ApiModel';
import { orderBy } from './ArrayHelper';

export function ipV4Addresses(server: Server): Network[] {
    return server.networks['v4'];
}

export function ipV6Addresses(server: Server): Network[] {
    return server.networks['v6'];
}

export function isPrivateAddress(intf: Network): boolean {
    return intf.type === 'private';
}

export function isPublicAddress(network: Network): boolean {
    return !isPrivateAddress(network);
}

export function privateAddresses(interfaces: Network[]): Network[] {
    return interfaces.filter(isPrivateAddress);
}

export function publicAddresses(interfaces: Network[]): Network[] {
    return interfaces.filter(isPublicAddress);
}

export function primaryInterface(server: Server): Network | undefined {
    return orderBy(ipV4Addresses(server), (i) => (i.type === 'private' ? 1 : -1))[0];
}

export type Ip6AddressType = 'SLAAC' | 'Routed Range';

export function ipv6AddressType(intf: Network): Ip6AddressType {
    //detect slaac ip. netmask is a LongOrString, so it may arrive as the string "64"; normalise
    //before comparing so the string form classifies identically to the number.
    if (Number(intf.netmask) === 64) {
        const parts = intf.ip_address.split(':');
        if (parts.length > 2) {
            if (
                parts[parts.length - 2]?.toLowerCase().startsWith('fe') &&
                parts[parts.length - 3]?.toLowerCase().endsWith('ff')
            ) {
                return 'SLAAC';
            }
        }
    }

    //we only have 1 other type.
    return 'Routed Range';
}

export function serverHasReverseNameserverOverride(server: Server): boolean {
    return (server.networks.ipv6_reverse_nameservers ?? []).length > 0;
}

/**
 * The address as it is shown to the customer. A SLAAC address is a single host address, so
 * the /64 the API reports for it would read as a delegated prefix; only a routed range is
 * displayed with its prefix length.
 */
export function ipv6DisplayAddress(intf: Network): string {
    if (intf.netmask === null || intf.netmask === undefined || ipv6AddressType(intf) === 'SLAAC')
        return intf.ip_address;
    return `${intf.ip_address}/${intf.netmask}`;
}
export function interfaceByIp(server: Server, ip?: string): Network | undefined {
    if (!ip) return undefined;
    return privateAddresses(ipV4Addresses(server)).find((i) => i.ip_address === ip);
}

export function natTargetInterface(server: Server, intf: Network): Network | undefined {
    return interfaceByIp(server, intf.nat_target ?? undefined);
}

export function alternateIps(server: Server) {
    const prim = primaryInterface(server);
    // all public ipv4 addresses except for primary
    return publicAddresses(ipV4Addresses(server)).filter((i) => i.ip_address !== prim?.ip_address);
}

export function isNatVPC(server: Server): boolean {
    return !!server.vpc_id && !server.networks.separate_private_network_interface;
}
